package com.exura.pages.register

import com.exura.features.localization.domain.model.BasicLangSet
import com.exura.features.localization.domain.model.Language
import com.exura.features.localization.domain.model.Localization

interface RegisterLang {
    val fields: Fields
    val accountTypes: AccountTypes
    val messages: Messages
    val actions: Actions
    val errors: Errors
    
    val pageTitle: String
    val accountTypePrompt: String
    
    interface Fields {
        val emailAddress: String
        val fullName: String
        val specialization: String
        val fieldOfActivity: String
        val contactPhoneNumber: String
    }
    
    interface AccountTypes {
        val doctor: String
        val organization: String
    }
    
    interface Messages {
        val alreadyHaveAccount: String
        val registrationSuccessful: String
        val thankYouForRegistering: String
        val accountPendingApproval: String
    }
    
    interface Actions {
        val register: String
        val returnToLogin: String
        val login: String
    }
    
    interface Errors {
        val registrationFailed: String
    }
}

data object RegisterPolish : RegisterLang {
    override val fields = Fields
    override val accountTypes = AccountTypes
    override val messages = Messages
    override val actions = Actions
    override val errors = Errors
    
    override val pageTitle: String = "Utwórz nowe konto"
    override val accountTypePrompt: String = "Jestem..."
    
    data object Fields : RegisterLang.Fields {
        override val emailAddress: String = "Adres e-mail"
        override val fullName: String = "Imię i nazwisko"
        override val specialization: String = "Specjalizacja (opcjonalnie)"
        override val fieldOfActivity: String = "Dziedzina działalności (opcjonalnie)"
        override val contactPhoneNumber: String = "Numer telefonu kontaktowego (opcjonalnie)"
    }
    
    data object AccountTypes : RegisterLang.AccountTypes {
        override val doctor: String = "Lekarzem"
        override val organization: String = "Reprezentantem placówki"
    }
    
    data object Messages : RegisterLang.Messages {
        override val alreadyHaveAccount: String = "Masz już konto?"
        override val registrationSuccessful: String = "Rejestracja zakończona sukcesem!"
        override val thankYouForRegistering: String = "Dziękujemy za rejestrację w Exura."
        override val accountPendingApproval: String =
            "Twoje konto oczekuje teraz na zatwierdzenie. Nasz zespół przeprowadza proces weryfikacji, który zwykle trwa 3-4 dni. Spodziewaj się kontaktu od naszego przedstawiciela w trakcie tego procesu."
    }
    
    data object Actions : RegisterLang.Actions {
        override val register: String = "Zarejestruj się"
        override val returnToLogin: String = "Powrót"
        override val login: String = "Zaloguj się"
    }
    
    data object Errors : RegisterLang.Errors {
        override val registrationFailed: String = "Rejestracja nie powiodła się. Spróbuj ponownie."
    }
}

data object RegisterEnglish : RegisterLang {
    override val fields = Fields
    override val accountTypes = AccountTypes
    override val messages = Messages
    override val actions = Actions
    override val errors = Errors
    
    override val pageTitle: String = "Create New Account"
    override val accountTypePrompt: String = "I'm a..."
    
    data object Fields : RegisterLang.Fields {
        override val emailAddress: String = "Email Address"
        override val fullName: String = "Full Name"
        override val specialization: String = "Specialization (optional)"
        override val fieldOfActivity: String = "Field of Activity (optional)"
        override val contactPhoneNumber: String = "Contact Phone Number (optional)"
    }
    
    data object AccountTypes : RegisterLang.AccountTypes {
        override val doctor: String = "Doctor"
        override val organization: String = "Organization Representative"
    }
    
    data object Messages : RegisterLang.Messages {
        override val alreadyHaveAccount: String = "Already have an account?"
        override val registrationSuccessful: String = "Registration successful!"
        override val thankYouForRegistering: String = "Thank you for registering with Exura."
        override val accountPendingApproval: String =
            "Your account is now pending approval. Our team conducts a verification process, which usually takes 3-4 days. Expect contact from our representative during this process."
    }
    
    data object Actions : RegisterLang.Actions {
        override val register: String = "Register"
        override val returnToLogin: String = "Return"
        override val login: String = "Login"
    }
    
    data object Errors : RegisterLang.Errors {
        override val registrationFailed: String = "Registration failed. Please try again."
    }
}

private val registerLangSet = BasicLangSet<RegisterLang>(
    mapOf(
        Language.POLISH to RegisterPolish,
        Language.ENGLISH to RegisterEnglish,
    )
)

val registerLocalization = Localization<RegisterLang>(registerLangSet)
